import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Copy, Calendar, KeyRound, BarChart3, X, Mail, Folder, FolderPlus, Download, FileText, Award, Image, Loader2, Pencil, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Brand } from "../components/Brand";
import { ResultsCharts } from "../components/ResultsCharts";
import { Certificate } from "../components/Certificate";
import { downloadUserCsv, printReport, downloadCohortCsv, downloadFolderResultsCsv } from "../lib/report";

const TABS = [
  { key: "codes", label: "Access Codes", icon: KeyRound },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "results", label: "Results", icon: BarChart3 },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("codes");

  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  if (!user) return <div className="p-12 text-[#1E293B]/60">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="sticky top-0 z-10 border-b border-[#E2E8F0] bg-[#F1F5F9]/95 px-6 py-4 backdrop-blur sm:px-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Brand subtitle="Admin Console" />
          <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout-btn"
            className="flex items-center gap-1.5 text-sm text-[#1E293B]/60 hover:text-[#2563EB]">
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8 sm:px-10">
        <div className="flex gap-2 rounded-full border border-[#E2E8F0] bg-white p-1" style={{ width: "fit-content" }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                tab === t.key ? "bg-[#1E293B] text-white" : "text-[#1E293B]/60 hover:text-[#1E293B]"}`}>
              <t.icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "codes" && <CodesTab />}
          {tab === "schedule" && <ScheduleTab />}
          {tab === "results" && <ResultsTab />}
        </div>
      </main>
    </div>
  );
}

function CodesTab() {
  const [codes, setCodes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState("all"); // "all" | "unfiled" | folderId
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [newFolder, setNewFolder] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultsExporting, setResultsExporting] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/codes").then(({ data }) => setCodes(data)),
    api.get("/admin/folders").then(({ data }) => setFolders(data)),
  ]);
  useEffect(() => { load(); }, []);

  const targetFolder = selected === "all" || selected === "unfiled" ? null : selected;

  const create = async () => {
    setLoading(true);
    try {
      const qty = Math.max(1, Math.min(parseInt(quantity) || 1, 1000));
      const bulk = qty > 1;
      const { data } = await api.post("/admin/codes", {
        participant_name: bulk ? null : (name.trim() || null),
        email: bulk ? null : (email.trim() || null),
        quantity: qty,
        folder_id: targetFolder,
      });
      if (!bulk && email.trim()) {
        if (data.email_status === "sent") toast.success(`Code created & emailed to ${email.trim()}`);
        else toast.warning(`Code created, but email ${data.email_status || "was not sent"}`);
      } else {
        toast.success(`${data.count} access code${data.count > 1 ? "s" : ""} created`);
      }
      setName(""); setEmail(""); setQuantity(1);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const createFolder = async () => {
    if (!newFolder.trim()) return;
    try {
      const { data } = await api.post("/admin/folders", { name: newFolder.trim() });
      setNewFolder("");
      toast.success("Folder created");
      await load();
      setSelected(data.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const delFolder = async (id) => {
    if (!window.confirm("Delete this folder? Its codes will move to Unfiled.")) return;
    await api.delete(`/admin/folders/${id}`);
    if (selected === id) setSelected("all");
    toast.success("Folder deleted");
    load();
  };

  const moveCode = async (id, folder_id) => {
    await api.patch(`/admin/codes/${id}`, { folder_id: folder_id || null });
    toast.success("Moved");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this code and all its responses?")) return;
    await api.delete(`/admin/codes/${id}`);
    toast.success("Deleted");
    load();
  };

  const sendEmail = async (c) => {
    try {
      await api.post(`/admin/codes/${c.id}/send-email`);
      toast.success(`Emailed ${c.code} to ${c.email}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copy = (c) => { navigator.clipboard.writeText(c); toast.success(`Copied ${c}`); };

  const selectedFolderObj = folders.find((f) => f.id === selected);
  const [logoBusy, setLogoBusy] = useState(false);
  const [editName, setEditName] = useState(null); // {id, value}
  const [sched, setSched] = useState({ baseline_start: "", baseline_end: "", post_start: "", post_end: "" });
  const [schedSaving, setSchedSaving] = useState(false);

  useEffect(() => {
    if (selectedFolderObj) {
      const sch = selectedFolderObj.schedule || {};
      setSched({
        baseline_start: sch.baseline_start || "",
        baseline_end: sch.baseline_end || "",
        post_start: sch.post_start || "",
        post_end: sch.post_end || "",
      });
    }
    // eslint-disable-next-line
  }, [selected, folders]);

  const saveFolderSchedule = async () => {
    setSchedSaving(true);
    try {
      await api.put(`/admin/folders/${selectedFolderObj.id}/schedule`, {
        baseline_start: sched.baseline_start || null,
        baseline_end: sched.baseline_end || null,
        post_start: sched.post_start || null,
        post_end: sched.post_end || null,
      });
      toast.success("Folder schedule saved");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSchedSaving(false); }
  };

  const saveName = async () => {
    try {
      await api.patch(`/admin/codes/${editName.id}/name`, { participant_name: editName.value.trim() || null });
      toast.success("Name updated");
      setEditName(null);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadLogo = (file) => {
    if (!selectedFolderObj || !file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB)."); return; }
    setLogoBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.patch(`/admin/folders/${selectedFolderObj.id}`, { logo: reader.result });
        toast.success("Logo updated");
        await load();
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      finally { setLogoBusy(false); }
    };
    reader.onerror = () => { toast.error("Could not read the image."); setLogoBusy(false); };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    await api.patch(`/admin/folders/${selectedFolderObj.id}`, { logo: "" });
    toast.success("Logo removed");
    load();
  };

  const folderName = (id) => folders.find((f) => f.id === id)?.name || "Unfiled";

  const exportCsv = () => {
    if (visible.length === 0) { toast.error("No codes to export."); return; }
    const appUrl = window.location.origin;
    const header = ["Code", "Participant", "Email", "Folder", "Baseline done", "Post done", "Email sent", "Login URL"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = visible.map((c) => [
      c.code, c.participant_name || "", c.email || "", c.folder_id ? folderName(c.folder_id) : "Unfiled",
      `${c.baseline_done}/${c.total_tests}`, `${c.post_done}/${c.total_tests}`, c.email_sent_at ? "yes" : "no", appUrl,
    ].map(esc).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const scope = selected === "all" ? "all" : selected === "unfiled" ? "unfiled" : folderName(selected).replace(/\s+/g, "_");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `access-codes-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visible.length} code${visible.length > 1 ? "s" : ""}`);
  };

  const exportResults = async () => {
    if (visible.length === 0) { toast.error("No codes to export."); return; }
    const visibleCodes = new Set(visible.map((c) => c.code));
    setResultsExporting(true);
    try {
      const { data } = await api.get("/admin/results");
      const rows = data.filter((r) => visibleCodes.has(r.code));
      if (rows.length === 0) { toast.error("No results yet for these codes."); return; }
      const scope = selected === "all" ? "all" : selected === "unfiled" ? "unfiled" : folderName(selected).replace(/\s+/g, "_");
      downloadFolderResultsCsv(rows, scope);
      toast.success(`Exported results for ${rows.length} participant${rows.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error("Could not export results.");
    } finally { setResultsExporting(false); }
  };

  const visible = codes.filter((c) => {
    if (selected === "all") return true;
    if (selected === "unfiled") return !c.folder_id;
    return c.folder_id === selected;
  });

  const FolderBtn = ({ id, label, count, deletable }) => (
    <div className={`group flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${selected === id ? "bg-[#1E293B] text-white" : "text-[#1E293B]/70 hover:bg-white"}`}>
      <button onClick={() => setSelected(id)} data-testid={`folder-${id}`} className="flex flex-1 items-center gap-2 text-left">
        <Folder className="h-4 w-4" strokeWidth={1.5} /> {label}
        <span className={`ml-auto text-xs ${selected === id ? "text-white/60" : "text-[#1E293B]/40"}`}>{count}</span>
      </button>
      {deletable && (
        <button onClick={() => delFolder(id)} data-testid={`delete-folder-${id}`} className={`ml-2 opacity-0 transition-opacity group-hover:opacity-100 ${selected === id ? "text-white/70" : "text-[#2563EB]/70"}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  const bulk = (parseInt(quantity) || 1) > 1;

  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      {/* Folders sidebar */}
      <aside className="space-y-2">
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F1F5F9] p-2">
          <FolderBtn id="all" label="All codes" count={codes.length} />
          <FolderBtn id="unfiled" label="Unfiled" count={codes.filter((c) => !c.folder_id).length} />
          <div className="my-2 border-t border-[#E2E8F0]" />
          {folders.map((f) => <FolderBtn key={f.id} id={f.id} label={f.name} count={f.code_count} deletable />)}
        </div>
        <div className="flex gap-2">
          <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()}
            placeholder="New folder" data-testid="new-folder-name"
            className="w-full rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
          <button onClick={createFolder} data-testid="create-folder-btn" className="rounded-lg bg-[#1E293B] px-3 text-white"><FolderPlus className="h-4 w-4" /></button>
        </div>
      </aside>

      {/* Main */}
      <div>
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-6">
          <h2 className="font-display text-2xl text-[#1E293B]">Create access codes</h2>
          <p className="mt-1 text-sm text-[#1E293B]/60">
            Generate one code (optionally with name/email) or a batch. New codes go to{" "}
            <strong>{targetFolder ? folders.find((f) => f.id === targetFolder)?.name : "Unfiled"}</strong>.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <input value={name} onChange={(e) => setName(e.target.value)} disabled={bulk} placeholder="Participant name (optional)" data-testid="new-code-name"
              className="rounded-xl border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#2563EB] disabled:opacity-40" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={bulk} placeholder="Email (optional)" data-testid="new-code-email"
              className="rounded-xl border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#2563EB] disabled:opacity-40" />
            <div>
              <input type="number" min={1} max={1000} value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="new-code-quantity"
                className="w-full rounded-xl border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#2563EB]" />
              <span className="mt-1 block text-xs text-[#1E293B]/40">Quantity (bulk hides name/email)</span>
            </div>
          </div>
          <button onClick={create} disabled={loading} data-testid="create-code-btn"
            className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 font-semibold text-white transition-transform duration-200 hover:-translate-y-1 disabled:opacity-60">
            <Plus className="h-4 w-4" /> {bulk ? `Generate ${quantity} codes` : (email.trim() ? "Generate & email" : "Generate")}
          </button>
        </div>

        {selectedFolderObj && (
          <div className="mt-6 rounded-xl border border-[#E2E8F0] bg-white p-6" data-testid="folder-logo-panel">
            <h3 className="font-display text-xl text-[#1E293B]">{selectedFolderObj.name} — report logo</h3>
            <p className="mt-1 text-sm text-[#1E293B]/60">This logo prints on every PDF report and certificate for codes in this folder.</p>
            <div className="mt-4 flex items-center gap-4">
              {selectedFolderObj.logo ? (
                <img src={selectedFolderObj.logo} alt="Folder logo" className="max-h-16 rounded-lg border border-[#E2E8F0] object-contain p-1" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-[#E2E8F0] text-[#1E293B]/30"><Image className="h-6 w-6" strokeWidth={1.5} /></div>
              )}
              <label className="flex cursor-pointer items-center gap-1.5 rounded-full border border-[#1E293B]/20 px-4 py-2 text-sm text-[#1E293B] transition-transform duration-200 hover:-translate-y-0.5" data-testid="upload-logo-label">
                {logoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" strokeWidth={1.5} />}
                {selectedFolderObj.logo ? "Replace logo" : "Upload logo"}
                <input type="file" accept="image/*" hidden data-testid="upload-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
              {selectedFolderObj.logo && (
                <button onClick={removeLogo} data-testid="remove-logo-btn" className="text-sm text-[#2563EB] hover:underline">Remove</button>
              )}
            </div>

            <div className="mt-6 border-t border-[#E2E8F0] pt-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">
                <Calendar className="h-4 w-4" strokeWidth={1.5} /> Schedule for this folder
              </div>
              <p className="mt-1 text-sm text-[#1E293B]/60">Date windows for codes in <strong>{selectedFolderObj.name}</strong>. Leave blank to keep a round always open. Overrides the global schedule.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-[#E2E8F0] p-4">
                  <div className="font-display text-[#1E293B]">Baseline (Before)</div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-[#1E293B]/50">Opens</label>
                      <input type="date" min="2000-01-01" max="2100-12-31" value={sched.baseline_start} onChange={(e) => setSched((p) => ({ ...p, baseline_start: e.target.value }))} data-testid="folder-date-baseline_start"
                        className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-2 py-2 text-sm outline-none focus:border-[#2563EB]" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-[#1E293B]/50">Closes</label>
                      <input type="date" min="2000-01-01" max="2100-12-31" value={sched.baseline_end} onChange={(e) => setSched((p) => ({ ...p, baseline_end: e.target.value }))} data-testid="folder-date-baseline_end"
                        className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-2 py-2 text-sm outline-none focus:border-[#2563EB]" />
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-[#E2E8F0] p-4">
                  <div className="font-display text-[#1E293B]">Post (After)</div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-[#1E293B]/50">Opens</label>
                      <input type="date" min="2000-01-01" max="2100-12-31" value={sched.post_start} onChange={(e) => setSched((p) => ({ ...p, post_start: e.target.value }))} data-testid="folder-date-post_start"
                        className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-2 py-2 text-sm outline-none focus:border-[#2563EB]" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-[#1E293B]/50">Closes</label>
                      <input type="date" min="2000-01-01" max="2100-12-31" value={sched.post_end} onChange={(e) => setSched((p) => ({ ...p, post_end: e.target.value }))} data-testid="folder-date-post_end"
                        className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-2 py-2 text-sm outline-none focus:border-[#2563EB]" />
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={saveFolderSchedule} disabled={schedSaving} data-testid="save-folder-schedule-btn"
                className="mt-4 rounded-full bg-[#1E293B] px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                {schedSaving ? "Saving…" : "Save folder schedule"}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <h3 className="font-display text-lg text-[#1E293B]">
            {selected === "all" ? "All codes" : selected === "unfiled" ? "Unfiled" : folders.find((f) => f.id === selected)?.name}
            <span className="ml-2 text-sm text-[#1E293B]/40">({visible.length})</span>
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={exportResults} disabled={resultsExporting} data-testid="export-results-btn"
              className="flex items-center gap-1.5 rounded-full bg-[#1E293B] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              {resultsExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" strokeWidth={1.5} />} Export results
            </button>
            <button onClick={exportCsv} data-testid="export-csv-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#1E293B]/20 px-4 py-2 text-sm text-[#1E293B] transition-transform duration-200 hover:-translate-y-0.5">
              <Download className="h-4 w-4" strokeWidth={1.5} /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-3 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="codes-table">
              <thead>
                <tr className="border-b border-[#E2E8F0] text-left text-xs uppercase tracking-wider text-[#1E293B]/50">
                  <th className="p-4">Code</th><th className="p-4">Participant</th><th className="p-4">Email</th><th className="p-4">Folder</th><th className="p-4">B</th><th className="p-4">P</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#1E293B]/40">No codes here yet.</td></tr>}
                {visible.map((c) => (
                  <tr key={c.id} className="group border-b border-[#EAEEF3]" data-testid={`code-row-${c.code}`}>
                    <td className="p-4 font-mono font-semibold tracking-widest text-[#1E293B]">{c.code}</td>
                    <td className="p-4 text-[#1E293B]/70">
                      {editName?.id === c.id ? (
                        <span className="flex items-center gap-1">
                          <input value={editName.value} autoFocus onChange={(e) => setEditName({ ...editName, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditName(null); }}
                            data-testid={`name-input-${c.code}`}
                            className="w-36 rounded-lg border border-[#2563EB] px-2 py-1 text-sm outline-none" />
                          <button onClick={saveName} data-testid={`save-name-${c.code}`} className="rounded p-1 text-[#15803D] hover:bg-[#15803D]/10"><Check className="h-4 w-4" /></button>
                          <button onClick={() => setEditName(null)} className="rounded p-1 text-[#1E293B]/50 hover:bg-[#F1F5F9]"><X className="h-4 w-4" /></button>
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {c.participant_name || <span className="text-[#1E293B]/30">—</span>}
                          <button onClick={() => setEditName({ id: c.id, value: c.participant_name || "" })} data-testid={`edit-name-${c.code}`}
                            className="rounded p-1 text-[#1E293B]/40 opacity-0 transition-opacity hover:bg-[#F1F5F9] hover:text-[#2563EB] group-hover:opacity-100"><Pencil className="h-3.5 w-3.5" /></button>
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-[#1E293B]/70">{c.email ? <span>{c.email}{c.email_sent_at && <span className="ml-1 text-xs text-[#15803D]">· sent</span>}</span> : <span className="text-[#1E293B]/30">—</span>}</td>
                    <td className="p-4">
                      <select value={c.folder_id || ""} onChange={(e) => moveCode(c.id, e.target.value)} data-testid={`move-${c.code}`}
                        className="rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-xs text-[#1E293B]/70 outline-none focus:border-[#2563EB]">
                        <option value="">Unfiled</option>
                        {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    </td>
                    <td className="p-4 text-[#1E293B]/70">{c.baseline_done}/{c.total_tests}</td>
                    <td className="p-4 text-[#1E293B]/70">{c.post_done}/{c.total_tests}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1">
                        {c.email && <button onClick={() => sendEmail(c)} data-testid={`send-email-${c.code}`} title="Send email" className="rounded-lg p-2 text-[#15803D] hover:bg-[#15803D]/10"><Mail className="h-4 w-4" /></button>}
                        <button onClick={() => copy(c.code)} data-testid={`copy-${c.code}`} className="rounded-lg p-2 text-[#1E293B]/50 hover:bg-[#F1F5F9] hover:text-[#1E293B]"><Copy className="h-4 w-4" /></button>
                        <button onClick={() => del(c.id)} data-testid={`delete-${c.code}`} className="rounded-lg p-2 text-[#2563EB]/70 hover:bg-[#2563EB]/10 hover:text-[#2563EB]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScheduleTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        baseline_start: s.baseline_start || null, baseline_end: s.baseline_end || null,
        post_start: s.post_start || null, post_end: s.post_end || null,
      });
      toast.success("Schedule saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-[#1E293B]/60">Loading…</div>;
  const field = (k, v) => setS((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <p className="mb-4 text-sm text-[#1E293B]/60">Set the date windows during which participants can submit each round. Leave blank to keep a window always open.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          { title: "Baseline (Before)", sub: "Pre-departure assessment window", sk: "baseline_start", ek: "baseline_end" },
          { title: "Post (After)", sub: "Post-return assessment window", sk: "post_start", ek: "post_end" },
        ].map((w) => (
          <div key={w.sk} className="rounded-xl border border-[#E2E8F0] bg-white p-6">
            <h3 className="font-display text-xl text-[#1E293B]">{w.title}</h3>
            <p className="mt-1 text-sm text-[#1E293B]/60">{w.sub}</p>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">Opens</label>
                <input type="date" min="2000-01-01" max="2100-12-31" value={s[w.sk] || ""} onChange={(e) => field(w.sk, e.target.value)} data-testid={`date-${w.sk}`}
                  className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 outline-none focus:border-[#2563EB]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">Closes</label>
                <input type="date" min="2000-01-01" max="2100-12-31" value={s[w.ek] || ""} onChange={(e) => field(w.ek, e.target.value)} data-testid={`date-${w.ek}`}
                  className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 outline-none focus:border-[#2563EB]" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="save-schedule-btn"
        className="mt-6 rounded-full bg-[#2563EB] px-8 py-3 font-semibold text-white transition-transform duration-200 hover:-translate-y-1 disabled:opacity-60">
        {saving ? "Saving…" : "Save schedule"}
      </button>
    </div>
  );
}

function ResultsTab() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [cert, setCert] = useState(null);
  const [onlyBoth, setOnlyBoth] = useState(true);
  useEffect(() => { api.get("/admin/results").then(({ data }) => setRows(data)); }, []);

  const completedBoth = (r) =>
    r.baseline.completed_tests === r.baseline.total_tests &&
    r.post.completed_tests === r.post.total_tests;

  const exportRows = onlyBoth ? rows.filter(completedBoth) : rows;

  const exportCohort = () => {
    if (exportRows.length === 0) {
      toast.error(onlyBoth ? "No participants have completed both rounds yet." : "No participants to export.");
      return;
    }
    downloadCohortCsv(exportRows);
    toast.success(`Exported ${exportRows.length} participant${exportRows.length > 1 ? "s" : ""}`);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-[#1E293B]/70">
          <input type="checkbox" checked={onlyBoth} onChange={(e) => setOnlyBoth(e.target.checked)} data-testid="only-both-toggle"
            className="h-4 w-4 accent-[#2563EB]" />
          Only participants who completed both rounds ({rows.filter(completedBoth).length})
        </label>
        <button onClick={exportCohort} data-testid="export-cohort-btn"
          className="flex items-center gap-1.5 rounded-full bg-[#1E293B] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
          <Download className="h-4 w-4" strokeWidth={1.5} /> Export comparison ({exportRows.length})
        </button>
      </div>
      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
        <table className="w-full text-sm" data-testid="admin-results-table">
          <thead>
            <tr className="border-b border-[#E2E8F0] text-left text-xs uppercase tracking-wider text-[#1E293B]/50">
              <th className="p-4">Participant</th><th className="p-4">Code</th><th className="p-4">Baseline total</th><th className="p-4">Post total</th><th className="p-4">Change</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#1E293B]/40">No participants yet.</td></tr>}
            {rows.map((r) => {
              const change = Math.round((r.post.weighted_total - r.baseline.weighted_total) * 10) / 10;
              return (
              <tr key={r.code} className="border-b border-[#EAEEF3]" data-testid={`result-row-${r.code}`}>
                <td className="p-4 text-[#1E293B]">{r.name || <span className="text-[#1E293B]/30">unassigned</span>}</td>
                <td className="p-4 font-mono tracking-widest text-[#1E293B]/70">{r.code}</td>
                <td className="p-4 font-semibold text-[#1E293B]">{r.baseline.weighted_total} <span className="text-xs font-normal text-[#1E293B]/40">({r.baseline.completed_tests}/{r.baseline.total_tests})</span></td>
                <td className="p-4 font-semibold text-[#1E293B]">{r.post.weighted_total} <span className="text-xs font-normal text-[#1E293B]/40">({r.post.completed_tests}/{r.post.total_tests})</span></td>
                <td className={`p-4 font-semibold ${change > 0 ? "text-[#15803D]" : change < 0 ? "text-[#2563EB]" : "text-[#1E293B]/50"}`}>{change > 0 ? "+" : ""}{change}</td>
                <td className="p-4 text-right">
                  <button onClick={() => setDetail(r)} data-testid={`view-detail-${r.code}`}
                    className="rounded-full border border-[#1E293B]/20 px-4 py-1.5 text-sm text-[#1E293B] transition-transform duration-200 hover:-translate-y-0.5">View</button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[#1E293B]/40 p-4 sm:p-8" onClick={() => setDetail(null)}>
          <div className="w-full max-w-4xl rounded-2xl bg-[#F1F5F9] p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-report data-testid="result-detail-modal">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <img src="/gcrc-logo.png" alt="GCRC seal" className="h-16 w-16 object-contain" />
                {detail.logo && <img src={detail.logo} alt="Program logo" className="max-h-14 object-contain" />}
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#2563EB]">Study Abroad Global Experience Certification · {detail.code}</div>
                  <h2 className="font-display text-3xl text-[#1E293B]">{detail.name || "Unassigned"}</h2>
                  <div className="text-xs text-[#1E293B]/50">Report generated {new Date().toLocaleDateString()}</div>
                </div>
              </div>
              <div className="no-print flex items-center gap-2">
                <button onClick={() => downloadUserCsv(detail)} data-testid="download-csv-btn"
                  className="flex items-center gap-1.5 rounded-full border border-[#1E293B]/20 px-4 py-2 text-sm text-[#1E293B] transition-transform duration-200 hover:-translate-y-0.5">
                  <FileText className="h-4 w-4" strokeWidth={1.5} /> CSV
                </button>
                <button onClick={printReport} data-testid="download-pdf-btn"
                  className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                  <Download className="h-4 w-4" strokeWidth={1.5} /> PDF
                </button>
                <button onClick={() => setCert(detail)} data-testid="certificate-btn"
                  className="flex items-center gap-1.5 rounded-full bg-[#1E293B] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                  <Award className="h-4 w-4" strokeWidth={1.5} /> Certificate
                </button>
                <button onClick={() => setDetail(null)} data-testid="close-detail-btn" className="rounded-full p-2 text-[#1E293B]/60 hover:bg-white"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <ResultsCharts report={detail} />
          </div>
        </div>
      )}
      {cert && <Certificate report={cert} onClose={() => setCert(null)} />}
    </div>
  );
}
